import React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import Icon, { NameTypes } from '../Icon/Icon';
import Typography from '../Typography/Typography';
import { ButtonPropAs, ButtonPropIconPosition, SizeTypes } from './ButtonTypes';
import HTMLComment from 'react-html-comment';
import { TypographyVariantTypes } from '../Typography/TypographyTypes';

const buttonVariants = cva(['rounded max-w-full outline-none transition-all justify-center flex items-center overflow-hidden'], {
    variants: {
        variant: {
            primary: [
                'bg-blue border border-blue-700 text-white stroke-[0.5px] stroke-white',
                'hover:bg-blue-900 hover:border-blue',
                'focus-visible:focus-outline',
                'disabled:bg-blue-10 disabled:text-blue-100 disabled:border-blue-10 disabled:hover:bg-blue-10',
                'group-[.has-accent-bg]:bg-darkblue group-[.has-accent-bg]:border group-[.has-accent-bg]:border-darkblue-400',
                'group-[.has-accent-bg]:hover:bg-darkblue-400',
                'group-[.has-accent-bg]:focus-visible:accent-color-focus-outline group-[.has-dark-bg]:focus-visible:accent-color-focus-outline',
                'group-[.has-accent-bg]:disabled:bg-darkblue-300 group-[.has-accent-bg]:disabled:text-darkblue-200',
            ],
            secondary: [
                'bg-white border border-blue-100 text-blue',
                'hover:bg-blue-10 hover:border-blue-300',
                'focus-visible:focus-outline',
                'disabled:bg-white disabled:border-blue-100 disabled:text-blue-100 disabled:hover:bg-white',
                'group-[.has-accent-bg]:bg-transparent group-[.has-accent-bg]:border group-[.has-accent-bg]:border-darkblue-300 group-[.has-accent-bg]:text-white',
                'group-[.has-accent-bg]:hover:border-bg-blue-200 group-[.has-accent-bg]:hover:bg-darkblue-400',
                'group-[.has-accent-bg]:focus-visible:accent-color-focus-outline group-[.has-dark-bg]:focus-visible:accent-color-focus-outline',
                'group-[.has-accent-bg]:disabled:text-darkblue-300 group-[.has-accent-bg]:disabled:border-bg-darkblue-400',
                '[.has-bg-blue_&]:!bg-transparent [.has-bg-blue_&]:!border-white/25',
                '[.has-bg-blue_&]:hover:!border-white/25 [.has-bg-blue_&]:hover:!bg-white/10',
            ],
            highlight: [
                'bg-turquoise border border-turquoise-700 text-darkblue',
                'hover:bg-darkblue hover:border-darkblue-300 hover:text-white',
                'focus-visible:focus-outline',
                'disabled:bg-turquoise-100 disabled:border-turquoise-200 disabled:text-darkblue-200 disabled:hover:bg-turquoise-100',
            ],
            blank: [''],
        },
        overlay: {
            true: ['before:absolute before:inset-0 before:z-1'],
        },
        size: {
            sm: 'py-3 px-2',
            md: 'p-[14px]',
            lg: 'p-6',
        },
        widthMobile: {
            full: 'w-full md:w-fit',
            fitContent: 'w-fit',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
        overlay: false,
        widthMobile: 'full',
    },
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLElement>, VariantProps<typeof buttonVariants> {
    href?: string;
    icon?: (typeof NameTypes)[number];
    iconPosition?: ButtonPropIconPosition;
    as?: ButtonPropAs;
}

const Button: React.FC<ButtonProps> = ({
    className,
    variant,
    overlay,
    size,
    widthMobile,
    as: AsTag = 'button',
    children,
    icon,
    iconPosition = 'before',
    ...props
}) => {
    return (
        <>
            <HTMLComment text={`@component: Button - Start (variant: ${variant})`} />
            {className && <HTMLComment text={`Additional classes from component "${className}"`} />}
            <AsTag key={AsTag} className={twMerge(buttonVariants({ variant, size, overlay, className, widthMobile }))} {...props}>
                {!children && (
                    <span className={'sr-only'}>
                        <HTMLComment text={'Add a label that describes the function of the button'} />
                    </span>
                )}
                <span className="empty:hidden">{icon && iconPosition === 'before' && <Icon key={'a'} name={icon} size={SizeTypes.sm} />}</span>
                {children && (
                    <Typography variant={TypographyVariantTypes.button} as="span" className={size === 'lg' ? 'px-2' : 'px-1'}>
                        {children}
                    </Typography>
                )}
                <span className="empty:hidden">{icon && iconPosition === 'after' && <Icon key={'b'} name={icon} size={SizeTypes.sm} />}</span>
            </AsTag>
            <HTMLComment text={'@component: Button - End'} />
        </>
    );
};

export default Button;
